import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { Form, FormControl } from "react-bootstrap";

const RawInventorySection = ({ baseUrl, config }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rawInventory, setRawInventory] = useState([]);
  const [itemName, setItemName] = useState("");
  const [searchText, setSearchText] = useState("");

  const fetchRawInventory = async () => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${baseUrl}/api/Inventory/v2.0/getRawInventoryMaster`,
        {},
        config
      );

      if (res?.data?.responseCode === "00") {
        setRawInventory(res?.data?.response || []);
      } else {
        setRawInventory([]);
        toast.error(res?.data?.responseMessage || "Failed to fetch raw inventory");
      }
    } catch (error) {
      setRawInventory([]);
      Swal.fire("Error", "Unable to fetch raw inventory", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleAddRawInventory = async (e) => {
    e.preventDefault();

    if (!itemName.trim()) {
      toast.error("Please enter item name");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        itemName: itemName.trim(),
      };

      const res = await axios.post(
        `${baseUrl}/api/Inventory/v2.0/addRawInventoryMaster`,
        payload,
        config
      );

      if (res?.data?.responseCode === "00") {
        toast.success(res?.data?.responseMessage || "Raw inventory item added");
        setItemName("");
        fetchRawInventory();
      } else {
        Swal.fire("Error", res?.data?.responseMessage || "Failed to add item", "error");
      }
    } catch (error) {
      Swal.fire("Error", "Something went wrong while adding item", "error");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    fetchRawInventory();
  }, []);

  const filteredRawInventory = useMemo(() => {
    if (!searchText.trim()) return rawInventory;

    return rawInventory.filter((item) =>
      item?.item_name?.toLowerCase().includes(searchText.toLowerCase())
    );
  }, [rawInventory, searchText]);

  return (
    <div className="mt-4">
      {/* Top bar */}
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3">
        <div style={{ minWidth: "260px", maxWidth: "320px", width: "100%" }}>
          <Form>
            <FormControl
              type="text"
              placeholder="Search raw inventory"
              className="bg-light"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </Form>
        </div>

        <form
          onSubmit={handleAddRawInventory}
          className="d-flex align-items-center gap-2 flex-wrap"
        >
          <FormControl
            type="text"
            placeholder="Enter raw inventory item name"
            className="bg-light"
            style={{ minWidth: "280px" }}
            value={itemName}
            onChange={(e) => setItemName(e.target.value)}
          />
          <button
            type="submit"
            className="btn btn-success"
            disabled={saving}
          >
            {saving ? "Adding..." : "Add Raw Item"}
          </button>
        </form>
      </div>

      {/* Table */}
      <div className="table-responsive">
        <table className="table table-bordered align-middle bg-white">
          <thead>
            <tr>
              <th style={{ width: "100px" }}>ID</th>
              <th>Item Name</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="2" className="text-center py-4">
                  Loading...
                </td>
              </tr>
            ) : filteredRawInventory.length === 0 ? (
              <tr>
                <td colSpan="2" className="text-center py-4">
                  No raw inventory found
                </td>
              </tr>
            ) : (
              filteredRawInventory.map((item) => (
                <tr key={item.id}>
                  <td>{item.id}</td>
                  <td>{item.item_name}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RawInventorySection;